import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Modal";
import SearchSelect from "../../components/SearchSelect";
import { errorMessage } from "../../services";
import { getOwners } from "../../services/owners";
import { createSite, getStartingPoints } from "../../services/sites";
import { Spinner } from "../../components/Loader";

// Create a website for an existing customer (store owner). A Sub Admin's website uses up one licence.
// A Super Admin also chooses where it starts: a demo store (its store type, design and, abroad, currency) or a custom
// website of a store type.
export default function AddSiteModal({ user, onClose, onCreated, onNeedCustomer }) {
  const isSuper = user.role === "superadmin";
  const [form, setForm] = useState({ name: "", ownerId: "", plan: "trial", start: "demo", demo: "", template: "custom", category: "", layout: "" });
  const [starts, setStarts] = useState({ demos: [], types: [] });
  const [owner, setOwner] = useState(null); // the chosen customer, as a search option
  const [hasCustomers, setHasCustomers] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (!isSuper) return undefined;
    let live = true;
    getStartingPoints()
      .then(({ data }) => live && setStarts(data))
      .catch((err) => live && setError(errorMessage(err)));
    return () => { live = false; };
  }, [isSuper]);

  const chosenDemo = starts.demos.find((d) => d.slug === form.demo);
  const demoOption = (d) => ({ value: d.slug, label: d.name, hint: d.currency === "INR" ? `India · ${d.type}` : `${d.country || "International"} · ${d.type} · ${d.currency}` });

  // Customers are searched on the server as you type; demo stores are all loaded, so they are filtered here
  const searchCustomers = useCallback(
    (query) => getOwners({ search: query, limit: 20, status: "active" }).then(({ data }) => data.owners.map((o) => ({ value: o.id, label: o.name, hint: o.email || o.phone }))),
    []
  );
  const searchDemos = useCallback((query) => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return Promise.resolve(starts.demos.map(demoOption).filter((o) => words.every((w) => `${o.label} ${o.hint}`.toLowerCase().includes(w))));
  }, [starts.demos]);

  useEffect(() => {
    let live = true;
    getOwners({ limit: 1, status: "active" })
      .then(({ data }) => live && setHasCustomers(data.owners.length > 0))
      .catch((err) => live && setError(errorMessage(err)));
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.ownerId) return setError("Choose the customer this website is for.");
    if (isSuper && form.start === "demo" && !form.demo) return setError("Choose the demo store to start from.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), ownerId: form.ownerId };
      if (isSuper) {
        payload.plan = form.plan;
        if (form.start === "demo") Object.assign(payload, { demo: form.demo, layout: chosenDemo && chosenDemo.currency === "INR" ? form.layout : "" });
        else Object.assign(payload, { template: form.template, layout: form.layout, ...(form.category.trim() ? { category: form.category.trim() } : {}) });
      }
      const { data } = await createSite(payload);
      onCreated(data.message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal eyebrow="NEW WEBSITE" title="Add website" onClose={onClose}
      description={isSuper ? "Creates a storefront for a customer." : "Uses one website licence from your bulk purchase."}>
      <form onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <label className="admin-field">Store name
          <input required minLength={2} maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fresh Bakery" />
        </label>
        <div className="admin-field">
          <SearchSelect
            label="Customer"
            placeholder="Search by name, email or phone…"
            emptyText="No customer matches"
            selected={owner}
            loadOptions={searchCustomers}
            onChange={(option) => { setOwner(option); setForm((f) => ({ ...f, ownerId: option.value })); }}
          />
        </div>
        {!hasCustomers && (
          <p className="muted">A website needs a customer first. <button type="button" className="text-btn" onClick={onNeedCustomer}>Add a customer →</button></p>
        )}
        {isSuper && (
          <>
            <div className="admin-field">Start from
              <div className="price-mode" role="radiogroup" aria-label="Start from">
                <label><input type="radio" name="start" value="demo" checked={form.start === "demo"} onChange={set("start")} /> A demo store</label>
                <label><input type="radio" name="start" value="custom" checked={form.start === "custom"} onChange={set("start")} /> Custom website</label>
              </div>
            </div>
            {form.start === "demo" ? (
              <div className="admin-field">
                <SearchSelect
                  label="Demo store"
                  placeholder="Search by name, type or country…"
                  emptyText={starts.demos.length ? "No demo store matches" : "Loading demo stores…"}
                  selected={chosenDemo ? demoOption(chosenDemo) : null}
                  loadOptions={searchDemos}
                  onChange={(option) => setForm((f) => ({ ...f, demo: option.value }))}
                />
                <small className="field-help">The new website gets this demo&apos;s store type and design (and currency, for a store abroad), with placeholder products for the customer to replace.</small>
                {chosenDemo && chosenDemo.domain && <small className="field-help">See it live: <a href={`https://${chosenDemo.domain}`} target="_blank" rel="noopener noreferrer">{chosenDemo.domain}</a></small>}
              </div>
            ) : (
              <>
                <label className="admin-field">Business <small className="field-help">What the store sells, e.g. Handmade leather wallets</small>
                  <input maxLength={60} value={form.category} onChange={set("category")} placeholder="e.g. Handmade leather wallets" />
                </label>
                <label className="admin-field">Store type <small className="field-help">Decides the sample layout and the storefront&apos;s wording</small>
                  <select value={form.template} onChange={set("template")}>
                    {starts.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
              </>
            )}
            {(form.start === "custom" || !chosenDemo || chosenDemo.currency === "INR") && (
              <label className="admin-field">Design
                <select value={form.layout} onChange={set("layout")}>
                  <option value="">Classic</option>
                  <option value="v2">Studio</option>
                </select>
              </label>
            )}
          </>
        )}
        {isSuper && (
          <label className="admin-field">Starting plan
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option value="trial">Free trial</option>
              <option value="monthly">Monthly (payment received)</option>
              <option value="yearly">Yearly (payment received)</option>
            </select>
          </label>
        )}
        <div className="product-modal-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Creating</> : "Create website"}</button>
        </div>
      </form>
    </Modal>
  );
}
